import { getDateOnly } from "@components/date-only"
import { Box, Group, Loader, Stack, useMantineColorScheme } from "@mantine/core"
import { useIntersection } from "@mantine/hooks"
import dayjs from "dayjs"
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { useBatchedWeekStatus } from "@/-dataloaders/week-status"
import { useBatchedCalendarEvents } from "@/-dataloaders/calendar-events"
import { DayEventsList } from "../DayEventsList"
import { useBreakpoints } from "@/utils/use-breakpoints"
import { DutyRosterPeriodStatusBadge } from "../DutyRosterPeriodStatusBadge"
import { CalendarWeekPeriod } from "@components/CalendarWeekPeriod"

interface Props {
  startDate: Date
  onStartDateChange?: (date: Date) => void
}

export interface CalendarWeekListHandle {
  scrollToToday: () => void
}

export const CalendarWeekList = forwardRef<CalendarWeekListHandle, Props>(
  function CalendarWeekList({ startDate, onStartDateChange }, ref) {
    const [internalStartDate, setInternalStartDate] = useState(startDate)
    const hasScrolledRef = useRef(false)

    useEffect(() => {
      setInternalStartDate(startDate)
      hasScrolledRef.current = false
    }, [startDate])
    const [minWeekOffset, setMinWeekOffset] = useState(-5)
    const [maxWeekOffset, setMaxWeekOffset] = useState(5)
    const containerRef = useRef<HTMLDivElement>(null)
    const previousScrollHeightRef = useRef(0)
    const loadingRef = useRef(false)

    const { ref: firstWeekElementRef, entry: firstWeekEntry } = useIntersection(
      {
        threshold: 0.1,
      }
    )

    const { ref: lastWeekElementRef, entry: lastWeekEntry } = useIntersection({
      threshold: 0.1,
    })

    const { ref: currentWeekViewRef, entry: currentWeekEntry } =
      useIntersection<HTMLDivElement>({
        threshold: 1,
        root: containerRef.current,
      })

    const { ref: todayWeekViewRef, entry: todayWeekEntry } =
      useIntersection<HTMLDivElement>({
        threshold: 1,
        root: containerRef.current,
      })

    // Load more weeks when top is in view
    useEffect(() => {
      if (firstWeekEntry?.isIntersecting && !loadingRef.current) {
        loadingRef.current = true

        const container = containerRef.current
        if (container) {
          previousScrollHeightRef.current = container.scrollHeight
        }

        setMinWeekOffset((prev) => prev - 2)

        setTimeout(() => {
          loadingRef.current = false
        }, 100)
      }
    }, [firstWeekEntry?.isIntersecting])

    // Load more weeks when bottom is in view
    useEffect(() => {
      if (lastWeekEntry?.isIntersecting) {
        setMaxWeekOffset((prev) => prev + 2)
      }
    }, [lastWeekEntry?.isIntersecting])

    // Adjust scroll position when weeks are added at the top
    useEffect(() => {
      const container = containerRef.current
      if (!container) return

      const newScrollHeight = container.scrollHeight
      const previousHeight = previousScrollHeightRef.current

      if (previousHeight > 0 && newScrollHeight > previousHeight) {
        const heightDifference = newScrollHeight - previousHeight
        container.scrollTop += heightDifference
      }
    }, [minWeekOffset])

    // Generate weeks based on current offsets
    const weeks: { internalStartDate: Date; endDate: Date }[] =
      React.useMemo(() => {
        const start = dayjs(internalStartDate).startOf("week")
        const end = dayjs(internalStartDate).endOf("week")

        const weeks = []

        for (let i = minWeekOffset; i <= maxWeekOffset; i++) {
          const weekStart = start.add(i, "week").toDate()
          const weekEnd = end.add(i, "week").toDate()
          weeks.push({ internalStartDate: weekStart, endDate: weekEnd })
        }
        return weeks
      }, [internalStartDate, minWeekOffset, maxWeekOffset])

    // Fill page with weeks until scroll is available
    useEffect(() => {
      const container = containerRef.current
      if (!container) return

      const checkAndFillPage = () => {
        const scrollHeight = container.scrollHeight
        const clientHeight = container.clientHeight

        // If content doesn't fill the page, add more weeks
        if (scrollHeight <= clientHeight) {
          setMaxWeekOffset((prev) => prev + 5)
        }
      }

      // Check after render
      const timer = setTimeout(checkAndFillPage, 0)
      return () => clearTimeout(timer)
    }, [weeks.length])

    // Scroll to current start date on initial render
    useLayoutEffect(() => {
      if (currentWeekEntry?.target && !hasScrolledRef.current) {
        hasScrolledRef.current = true
        // Delay scroll to ensure DOM is fully rendered
        setTimeout(() => {
          currentWeekEntry?.target.scrollIntoView({ block: "center" })
        }, 50)
      }
    }, [weeks.length, currentWeekEntry, internalStartDate])

    const handleScrollToToday = () => {
      if (todayWeekEntry?.target) {
        todayWeekEntry.target.scrollIntoView({
          block: "center",
          behavior: "smooth",
        })
      } else {
        const today = dayjs().startOf("isoWeek").toDate()
        setInternalStartDate(today)
        onStartDateChange?.(today)
      }
    }

    useImperativeHandle(ref, () => ({
      scrollToToday: handleScrollToToday,
    }))

    return (
      <Box style={{ position: "relative", height: "100%" }}>
        <Stack
          gap={0}
          ref={containerRef}
          style={{
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {weeks.map((week, index) => {
            const startDateWeekStart = dayjs(internalStartDate)
              .startOf("week")
              .toDate()
            const isCurrent = dayjs(week.internalStartDate).isSame(
              startDateWeekStart,
              "week"
            )
            const isFirst = index === 0
            const isLast = index === weeks.length - 1

            return (
              <div
                key={week.internalStartDate.toISOString()}
                ref={
                  isCurrent
                    ? currentWeekViewRef
                    : isFirst
                      ? firstWeekElementRef
                      : isLast
                        ? lastWeekElementRef
                        : null
                }
              >
                <WeekEvents
                  todayRef={todayWeekViewRef}
                  weekStart={week.internalStartDate}
                  weekEnd={week.endDate}
                />
              </div>
            )
          })}
        </Stack>
      </Box>
    )
  }
)

interface WeekEventsProps {
  weekStart: Date
  weekEnd: Date
}

function WeekEvents({
  weekStart,
  weekEnd,
  todayRef,
}: WeekEventsProps & { todayRef: (instance: HTMLDivElement | null) => void }) {
  const { data: events, isPending: isEventsPending } = useBatchedCalendarEvents(
    {
      startDate: getDateOnly(weekStart),
      endDate: getDateOnly(weekEnd),
      method: "AllCount",
      withPossibleCasts: true,
      onlyReleasedDuties: false,
      withPersStatus: true,
    }
  )
  const { data: weekStatusId, isPending: isWeekStatusPending } =
    useBatchedWeekStatus(getDateOnly(weekStart))
  const { t } = useTranslation("app_server")
  const { colorScheme } = useMantineColorScheme()
  const dayEvents = React.useMemo(() => {
    let res = events

    const daysInWeek = 7
    const eventsMap = new Map(
      events?.map(([date, ev]) => [dayjs(date).format("YYYY-MM-DD"), ev])
    )
    res = []
    for (let i = 0; i < daysInWeek; i++) {
      const currentDate = dayjs(weekStart).add(i, "day").format("YYYY-MM-DD")
      const existingEvent = eventsMap.get(currentDate)
      if (existingEvent) {
        res.push([getDateOnly(dayjs(currentDate).toDate()), existingEvent])
      } else {
        res.push([getDateOnly(dayjs(currentDate).toDate()), []])
      }
    }

    return res
  }, [events, weekStart])
  const { xs } = useBreakpoints()

  return (
    <div
      style={{
        border: `1px solid ${colorScheme === "dark" ? "var(--mantine-color-gray-7)" : "var(--mantine-color-gray-3)"}`,
      }}
    >
      <Box
        w={"100%"}
        bg={
          colorScheme === "dark"
            ? "var(--mantine-color-gray-8)"
            : "var(--mantine-color-gray-1)"
        }
        style={{ position: "sticky", top: 0, zIndex: 1 }}
        px={"xs"}
      >
        <Group wrap={"nowrap"} justify={xs ? "space-between" : undefined}>
          <CalendarWeekPeriod date={weekStart} />
          {!!weekStatusId && (
            <DutyRosterPeriodStatusBadge
              statusId={weekStatusId}
              size={xs ? "sm" : "md"}
            />
          )}
          {isEventsPending || isWeekStatusPending ? (
            <Loader size={"xs"} />
          ) : null}
        </Group>
      </Box>
      <Box>
        {dayEvents && dayEvents.length > 0 ? (
          <DayEventsList
            todayRef={todayRef}
            renderEmptyDays
            divider
            events={dayEvents}
          />
        ) : (
          <Box px="xs">{t("NoAppointments")}</Box>
        )}
      </Box>
    </div>
  )
}
